import { useMemo, type ReactElement } from 'react';
import { creerClient } from '../../api/client.js';
import { COULEURS } from '../../app/styles.js';
import { ListeParDossier, useLargeurFenetre, type CelluleListe } from '../dossier/liste-par-dossier.js';
import { aTraiterDocuments, creerSourceListeDocuments, type LigneDocuments, type SourceListeDocuments } from './source-liste.js';

/** D09 sans dossier ouvert : les documents de tous les dossiers (équipe MAAT), comme la démo ; chaque ligne mène aux pièces du dossier. */
const MESSAGES = {
  intro: 'Choisissez un dossier pour classer, vérifier et envoyer en signature ses pièces.',
  chargement: 'Chargement des documents des dossiers en cours…',
  erreur: 'Les documents des dossiers n’ont pas pu être chargés. Réessayez dans un instant.',
  vide: 'Aucun dossier pour le moment.',
  indisponible: 'Non disponible',
  complet: 'Complet',
} as const;

const pluriel = (n: number, un: string, plusieurs: string): string => `${n} ${n > 1 ? plusieurs : un}`;
const puce = (couleur: string, fond: string) =>
  ({ display: 'inline-block', padding: '2px 8px', borderRadius: 8, fontSize: 14, marginRight: 4, color: couleur, background: fond }) as const;

function cellules(l: LigneDocuments): CelluleListe[] {
  const etat: ReactElement =
    l.etat === 'indisponible' ? (
      <span style={puce(COULEURS.ambre, COULEURS.ambreFond)}>{MESSAGES.indisponible}</span>
    ) : l.aVerifier + l.refusees + l.attendues === 0 ? (
      <span>{MESSAGES.complet}</span>
    ) : (
      <span>
        {l.aVerifier > 0 ? <span style={puce(COULEURS.ambre, COULEURS.ambreFond)}>{`${l.aVerifier} à vérifier`}</span> : null}
        {l.refusees > 0 ? <span style={puce(COULEURS.rouge, COULEURS.surface)}>{pluriel(l.refusees, 'refusée', 'refusées')}</span> : null}
        {l.attendues > 0 ? <span style={puce(COULEURS.marine, COULEURS.surface)}>{pluriel(l.attendues, 'attendue', 'attendues')}</span> : null}
      </span>
    );
  return [
    { titre: 'Pièces', contenu: l.etat === 'ok' ? String(l.pieces) : '—', nombre: true },
    { titre: 'À traiter', contenu: etat },
  ];
}

export function ListeDocuments({ source, largeur }: { source: SourceListeDocuments; largeur: number }): ReactElement {
  return (
    <ListeParDossier<LigneDocuments>
      nom="documents"
      titre="Documents"
      intro={MESSAGES.intro}
      chargement={MESSAGES.chargement}
      erreur={MESSAGES.erreur}
      vide={MESSAGES.vide}
      largeur={largeur}
      charger={source.charger}
      cellules={cellules}
      aTraiter={aTraiterDocuments}
      lien={(l) => ({ vers: `/documents?dossier=${encodeURIComponent(l.dossierId)}`, libelle: 'Ouvrir', aria: `Ouvrir les documents de ${l.titre}` })}
    />
  );
}

/** Branche la liste sur les routes réelles (même origine, cookie de session). */
export function ListeDocumentsReelle({ fetchApi }: { fetchApi?: typeof fetch } = {}): ReactElement {
  const largeur = useLargeurFenetre();
  const source = useMemo(() => creerSourceListeDocuments(creerClient({ fetch: fetchApi ?? ((...args) => globalThis.fetch(...args)) })), []);
  return <ListeDocuments source={source} largeur={largeur} />;
}
